import { AUTH_CONFIG, User } from './auth.config';
import { STATIC_AUTH_SYNC } from '../constants/routes';
import { PROBE_TIMEOUT_MS } from '../constants/timing';

/**
 * Probe outcome.
 * - 'active'   — Vue reported a live session
 * - 'empty'    — Vue reported no session (user is logged out on Vue)
 * - 'unknown'  — probe failed (Vue dev server down, CSP block, timeout)
 *
 * Callers treat 'unknown' as "don't change anything" so a transient outage
 * does not force the user out.
 */
export type VueSessionStatus = 'active' | 'empty' | 'unknown';

/**
 * Emulation state read from the Vue origin's localStorage during the probe
 * (reported by `check-session.html`). Lets the React app mirror a
 * Vue-started emulation into its own origin so an adopted session lands in
 * the SAME emulation the Vue side is running. `emulatedUserDetail` /
 * `emulatedUserName` travel as the raw localStorage strings — they are
 * mirrored verbatim, never re-serialized.
 */
export interface VueEmulationState {
  active: boolean;
  emulatedUserId: string | null;
  emulatedUserName: string | null;
  emulatedUserDetail: string | null;
}

export interface VueSessionProbeResult {
  status: VueSessionStatus;
  token: string | null;
  refreshToken: string | null;
  user: User | null;
  /** Raw `userDetail` JSON string (canonical backend record) or null. */
  userDetail: string | null;
  /**
   * Vue-origin emulation state, or null when the probe couldn't read it
   * (e.g. a Vue build that predates the emulation probe field).
   */
  emulation: VueEmulationState | null;
}

interface ProbeMessage {
  type: 'rw-session-probe';
  loggedIn: boolean;
  token?: string | null;
  refreshToken?: string | null;
  user?: User | null;
  userDetail?: string | null;
  emulation?: VueEmulationState | null;
}

const PROBE_MESSAGE_TYPE = 'rw-session-probe';

const UNKNOWN: VueSessionProbeResult = {
  status: 'unknown',
  token: null,
  refreshToken: null,
  user: null,
  userDetail: null,
  emulation: null,
};

/**
 * Ask the Vue ERP whether the user is still logged in there.
 *
 * Opens a hidden iframe at `${VUE_APP_URL}/auth-sync/check-session.html`.
 * That static page reads Vue's own-origin localStorage and posts a message
 * back. We match MessageEvent.source against our iframe's contentWindow so
 * another tab cannot impersonate the probe.
 */
export function probeVueSession(
  timeoutMs: number = PROBE_TIMEOUT_MS
): Promise<VueSessionProbeResult> {
  return new Promise((resolve) => {
    if (!AUTH_CONFIG.VUE_APP_URL) {
      resolve(UNKNOWN);
      return;
    }

    let expectedOrigin: string;
    try {
      expectedOrigin = new URL(AUTH_CONFIG.VUE_APP_URL).origin;
    } catch {
      resolve(UNKNOWN);
      return;
    }

    let settled = false;
    const iframe = document.createElement('iframe');
    iframe.style.display = 'none';
    iframe.setAttribute('aria-hidden', 'true');
    iframe.src = `${AUTH_CONFIG.VUE_APP_URL}${STATIC_AUTH_SYNC.checkSession}?t=${Date.now()}`;

    const cleanup = (): void => {
      window.removeEventListener('message', onMessage);
      try {
        document.body.removeChild(iframe);
      } catch {
        /* noop */
      }
    };

    const finish = (result: VueSessionProbeResult): void => {
      if (settled) return;
      settled = true;
      cleanup();
      resolve(result);
    };

    const onMessage = (event: MessageEvent): void => {
      if (event.origin !== expectedOrigin) return;
      if (event.source !== iframe.contentWindow) return;
      const data = event.data as ProbeMessage | undefined;
      if (!data || data.type !== PROBE_MESSAGE_TYPE) return;

      finish({
        status: data.loggedIn ? 'active' : 'empty',
        token: data.token ?? null,
        refreshToken: data.refreshToken ?? null,
        user: data.user ?? null,
        // Defensive parsing: an older Vue build that doesn't send these
        // fields degrades to null — adoption still works without them.
        userDetail: typeof data.userDetail === 'string' ? data.userDetail : null,
        emulation: data.emulation ?? null,
      });
    };

    window.addEventListener('message', onMessage);
    iframe.onerror = () => finish(UNKNOWN);
    setTimeout(() => finish(UNKNOWN), timeoutMs);

    document.body.appendChild(iframe);
  });
}
